/**
 * Un élément d'un constat peut-il recevoir une action donnée ?
 *
 * ## Pourquoi cette fonction est seule
 *
 * La règle existait en trois exemplaires — le registre côté serveur
 * (`services/actionsCorrectives/registre.js`), la modale de correction et la carte Radar — et
 * **le troisième avait divergé** : il ignorait `choixRequis`, si bien qu'un mot-clé en doublon
 * n'offrait aucun bouton sur sa ligne alors que la carte en annonçait un. L'écran se
 * contredisait lui-même.
 *
 * ⚠️ **La source de vérité reste `elementActionnable()` du serveur** : c'est lui qui décide ce
 * qu'il accepte d'écrire, et le navigateur ne fait qu'éviter d'afficher un bouton qui serait
 * refusé. Les deux doivent dire la même chose ; en cas de doute, c'est le serveur qui tranche,
 * et il refuse proprement en nommant ce qui manque.
 *
 * ## Les deux formes d'une cible
 *
 * | Cas | Ce que porte `cible` | Exemple |
 * |---|---|---|
 * | ordinaire | les identifiants exigés par la mutation | `{ adGroupId, adId }` |
 * | à choisir | une **liste** d'objets parmi lesquels trancher | `{ occurrences: [...] }` |
 *
 * Le second existe parce qu'un doublon ne se corrige pas en cochant une case : le mot-clé vit
 * dans plusieurs campagnes, et laquelle mettre en pause est un arbitrage que l'outil n'a pas à
 * faire. L'action déclare alors `choixRequis`, et l'écran fait choisir.
 *
 * ## La pertinence : avoir les identifiants ne suffit pas
 *
 * Un mot-clé à faible Quality Score porte toujours de quoi écrire, mais le geste qui le corrige
 * dépend de **laquelle** des trois composantes est en cause. L'action déclare alors
 * `pertinence: { champs, valeurs }` — pertinente si l'un des champs de la cible porte l'une des
 * valeurs. C'est une description et non une fonction, précisément pour qu'elle puisse voyager
 * jusqu'ici : voir `actionPertinente()` dans le registre, dont ceci est le miroir.
 */
export function actionPertinente(action, cible) {
  const regle = action?.pertinence;
  if (!regle) return true;
  if (!cible) return false;
  return (regle.champs || []).some(champ => (regle.valeurs || []).includes(cible[champ]));
}

export function elementActionnable(element, action) {
  if (!element?.cible || !action) return false;

  /*
   * ⚠️ **Un geste qui porte sur le constat n'arme aucune ligne.** Son `champsCible` est vide, et
   * `[].every(...)` vaut `true` : sans ce refus, un bouton apparaîtrait au bout de lignes que le
   * geste ne touche pas. Miroir de la même règle côté serveur.
   */
  if (action.surLeConstat) return false;

  if (!actionPertinente(action, element.cible)) return false;

  if (action.choixRequis) {
    const choix = element.cible[action.choixRequis];
    return Array.isArray(choix) && choix.length > 0;
  }

  return (action.champsCible || []).every(
    champ => element.cible[champ] !== undefined && element.cible[champ] !== null
  );
}

export default elementActionnable;
